"use client"

import { Button, Typography } from "@mui/material"
import {
  ContactDto,
  DriveTestReservationDto,
  ShowRoomDto,
  postContactDriveTestReservation,
  postContactEcommContact,
} from "api"
import FormikCheckboxField from "common/FormikCheckboxField"
import { useIsDesktop } from "common/MediaQueries"
import PrivacyPolicyCaption from "common/PrivacyPolicyCaption"
import {
  trackPersonalDetails,
  trackPopUpClicks,
  trackPopUpOpen,
  trackSuccessLead,
} from "common/analytics"
import FormikForm from "common/bit/form/FormikForm"
import { getUtmParamsAsKeyValueList } from "common/bit/utils/utmParams"
import { getFormName } from "common/getFormName"
import { postCallboxLead } from "common/postCallboxLead"
import { usePathname, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useState } from "react"
import FormErrorState from "reusable/formsComp/FormErrorState"
import FormInput from "reusable/formsComp/FormInput"
import FormSelect, { toFormSelectOptions } from "reusable/formsComp/FormSelect"
import FormSuccessState from "reusable/formsComp/FormSuccessState"
import ClassicPopUp from "reusable/popups/ClassicPopUp"
import styled from "styled-components"
import schemaTestDrive from "./SchemaTestDrive"
import schemaTestDriveContact from "./SchemaTestDriveContact"

const StyledClassicPopUp = styled(ClassicPopUp)<{
  $isModelPage?: boolean
  $isIframe?: boolean
}>`
  .MuiDialog-paper {
    ${(p) =>
      p?.$isIframe &&
      `
    width: 960px;
    max-width: 100% !important;
    ${p.theme.breakpoints.down("sm")} {
        &.mobile-margin{
          width: calc(100% - 30px);
        }
      }
    `}
  }
  ${(p) =>
    p?.$isModelPage &&
    `
  .MuiPaper-root {
    margin: 30px 0;
  }`}
`

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    .MuiFormControl-root {
      margin-top: 10px;
    }
  }
`
const StyledFormDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  width: 100%;
`
const StyledButton = styled(Button)`
  margin: 20px 0px;
  height: 45px;
  background: ${(p) => p?.theme?.colors?.black};
  color: white;
  :hover {
    background: ${(p) => p?.theme?.colors?.steel};
    outline: none;
  }
  &:focus-visible {
    border: 2px dashed white;
    outline: 3px dashed #0e3487;
    outline-offset: 2px;
  }
`
const StyledRowCol = styled.div<{ $type?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  gap: 2px;

  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    column-gap: 16px;
    margin-bottom: 30px;
  }

  ${(p) => p?.$type === "mail" && "margin-bottom: 20px !important;"}
`
const StyledWrap = styled.div`
  width: 100%;
  max-width: 320px;
`
const StyledRequiredFields = styled(Typography).attrs({ variant: "caption" })`
  color: ${(p) => p.theme.colors.mazdaRed};
  margin-left: auto;
`

const StyledFormSelect = styled(FormSelect)<{
  $isshop?: boolean
  $isModelPage?: boolean
}>`
  width: 296px;

  .MuiSelect-root {
    &:focus-visible {
      border: 0;
      outline: 0;
    }
  }

  ${(p) =>
    p?.$isshop &&
    `
    width: 296px;

    label {
      position: relative;
      top: ${p?.$isModelPage ? "16px" : "10px"};
      right: 10px;
    }

    label[class*="MuiInputLabel-shrink"] {
      transform: translate(8px, -6px) scale(0.75);
    }
  `}

  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

const StyledFormInput = styled(FormInput)`
  legend {
    width: 0;
  }
`

const StyledFormikCheckboxField = styled(FormikCheckboxField)`
  margin-top: 30px;
  span {
    user-select: none;
    font-size: 16px;
  }

  span[class*="PrivateSwitchBase-root"] {
    padding: 0 9px 9px 9px;
    &:focus {
      border: 2px dashed white;
      outline: 3px dashed #0e3487;
      outline-offset: 2px;
    }
  }
`

type itemProps = {
  id: string
  name: string
}

export enum FormStatus {
  Normal = "normal",
  Success = "success",
  Error = "error",
}

type PopupProps = {
  onClose: () => void
  open: boolean
  showRoomsList: ShowRoomDto[]
  modelsOptions?: {}[]
  contactForm?: boolean
  dialogClass?: string
  title?: string
  buttonText?: string
  formType?: "test-drive" | "contact"
  modelId?: number
  userInfo?: any
  analyticName?: string
  isshop?: boolean
}

const TestDrivePopupContent = ({
  onClose,
  open,
  showRoomsList,
  modelsOptions = [],
  contactForm,
  dialogClass,
  buttonText,
  formType,
  title,
  modelId,
  userInfo,
  analyticName,
  isshop,
}: PopupProps) => {
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.Normal)
  const showRoomsOptions = toFormSelectOptions(showRoomsList, "id", "name")
  const models = toFormSelectOptions(modelsOptions, "id", "name")

  const isDesktop = useIsDesktop()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isModelPage = pathname.startsWith("/model")

  const initialValues = {
    firstName: userInfo?.firstName || "",
    lastName: userInfo?.lastName || "",
    mobilePhone: userInfo?.mobilePhone || "",
    emailAddress: userInfo?.emailAddress || "",
    showRoom: "",
    over18: false,
    terms: false,
    isMailing: false,
    carModel: "",
    phoneNumber: userInfo?.mobilePhone || "",
    email: userInfo?.emailAddress || "",
    approveMailing: false,
  }

  useEffect(() => {
    if (open) trackPopUpOpen(analyticName || "test drive")
    else setFormStatus(FormStatus.Normal)
  }, [open])

  const checkboxLabel = `אני רוצה לקבל פניות ועדכונים במייל ו/או בווטסאפ ו/או בסמס מקבוצת דלק מוטורס על מבצעים, השקות, אירועים ועוד. ולהיכלל במאגר המידע של הקבוצה.`

  return (
    <StyledClassicPopUp
      open={open}
      onClose={onClose}
      title={title}
      dialogClass={dialogClass}
      hasError={formStatus === FormStatus.Error}
      $isModelPage={isModelPage}
    >
      <StyledWrapper>
        {formStatus === FormStatus.Normal && (
          <FormikForm
            validationSchema={
              contactForm ? schemaTestDriveContact : schemaTestDrive
            }
            initialValues={initialValues}
            onSubmit={async (values) => {
              const utmValues = getUtmParamsAsKeyValueList()

              // modelId from prop or query param
              const queryModelId = searchParams.get("modelId")
              const carModelId =
                modelId ||
                (queryModelId ? parseInt(queryModelId) : 0) ||
                parseInt(values.carModel) ||
                0

              const request =
                formType === "test-drive"
                  ? ({
                      carModelId,
                      showRoomId: values.showRoom
                        ? parseInt(values.showRoom)
                        : 0,
                      firstName: values.firstName,
                      lastName: values.lastName,
                      phone: values.phoneNumber,
                      email: values.email,
                      contactSource: isDesktop ? "Pc" : "Mobile",
                      utmValues,
                      approveMailingList: values.isMailing,
                      sid: window?.__ctm?.config?.sid || "",
                      formName: getFormName(formType),
                    } as DriveTestReservationDto)
                  : ({
                      fullName: `${values.firstName} ${values.lastName}`,
                      phone: values.phoneNumber,
                      description: "",
                      mediaChannelId: "",
                      branchId: Number(values.showRoom),
                      modelId: carModelId ?? null,
                      platform: isDesktop ? "Pc" : "Mobile",
                      isMailing: values?.isMailing,
                      utmValues,
                      emailAddress: values?.email,
                      sid: window?.__ctm?.config?.sid || "",
                      formName: getFormName(formType),
                    } as ContactDto)

              try {
                const { data } =
                  formType === "test-drive"
                    ? await postContactDriveTestReservation(
                        request as DriveTestReservationDto
                      )
                    : await postContactEcommContact(request as ContactDto)
                if (formType) postCallboxLead(formType, request)
                if (data?.body) {
                  trackSuccessLead(analyticName || "test drive", "lead_success")
                  trackPersonalDetails(
                    formType === "test-drive" ? "TestDrive" : "Meeting",
                    values.email,
                    values.phoneNumber,
                    false
                  )
                  setFormStatus(FormStatus.Success)
                } else setFormStatus(FormStatus.Error)
              } catch (err) {
                console.error(err)
                setFormStatus(FormStatus.Error)
              }
            }}
            render={(formik) => (
              <StyledFormDiv>
                <StyledRequiredFields>* שדות חובה</StyledRequiredFields>
                <StyledRowCol>
                  <StyledFormInput
                    name="firstName"
                    label="שם פרטי"
                    required
                    autoComplete="given-name"
                    onFocusCapture={() =>
                      trackPopUpClicks(
                        analyticName || "test drive",
                        "firstName"
                      )
                    }
                    marginTop={isDesktop ? 10 : 0}
                  />
                  <StyledFormInput
                    name="lastName"
                    label="שם משפחה"
                    required
                    autoComplete="family-name"
                    onFocusCapture={() =>
                      trackPopUpClicks(analyticName || "test drive", "lastName")
                    }
                    marginTop={isDesktop ? 10 : 0}
                  />
                </StyledRowCol>
                <StyledRowCol $type="mail">
                  <StyledFormInput
                    marginTop={0}
                    name="email"
                    label="כתובת מייל"
                    required
                    type="email"
                    autoComplete="email"
                    onFocusCapture={() =>
                      trackPopUpClicks(analyticName || "test drive", "email")
                    }
                  />
                  <StyledFormInput
                    marginTop={0}
                    name="phoneNumber"
                    label="טלפון נייד"
                    type="tel"
                    required
                    autoComplete="tel-national"
                    onFocusCapture={() =>
                      trackPopUpClicks(
                        analyticName || "test drive",
                        "phoneNumber"
                      )
                    }
                  />
                </StyledRowCol>
                {contactForm && (
                  <FormSelect
                    name="carModel"
                    required
                    label="בחירת דגם רכב"
                    values={models}
                    map={(item) => ({ id: item.id, name: item.name })}
                    onClick={() =>
                      trackPopUpClicks(analyticName || "test drive", "carModel")
                    }
                  />
                )}
                <StyledFormSelect
                  $isshop={isshop}
                  $isModelPage={isModelPage}
                  marginTop={0}
                  name="showRoom"
                  required
                  label="בחירת אולם תצוגה"
                  values={showRoomsOptions}
                  map={(item: itemProps) => ({ id: item.id, name: item.name })}
                  onClick={() =>
                    trackPopUpClicks(analyticName || "test drive", "showRoom")
                  }
                />
                <StyledFormikCheckboxField
                  name="isMailing"
                  label={checkboxLabel}
                  required
                  classes={{ root: "orderCheckbox" }}
                />
                <PrivacyPolicyCaption />
                <StyledWrap>
                  <StyledButton
                    disabled={formik.isSubmitting}
                    fullWidth
                    variant="text"
                    type="submit"
                  >
                    {buttonText || "חִזרו אליי לתיאום הנסיעה"}
                  </StyledButton>
                </StyledWrap>
              </StyledFormDiv>
            )}
          />
        )}
        {formStatus === FormStatus.Error && <FormErrorState />}
        {formStatus === FormStatus.Success && <FormSuccessState />}
      </StyledWrapper>
    </StyledClassicPopUp>
  )
}

const TestDrivePopup = (props: PopupProps) => (
  <Suspense fallback={null}>
    <TestDrivePopupContent {...props} />
  </Suspense>
)

export default TestDrivePopup
